import { useCookies } from 'react-cookie'
import { useNavigate, useParams } from 'react-router-dom'

import { Box, Typography } from '@mui/material'
import { Container, Section } from 'components'
import { InnerHtml } from 'components/ui'
import { useMappedTranslation, usePageTitle } from 'hooks'
import { Button } from 'oneui'
import i18n from 'utils/i18n'

import errorMap from './assets/json/error-mapping.json'

function ErrorPage() {
  const { errorCode } = useParams() as { errorCode: string }
  const t = useMappedTranslation()
  const navigate = useNavigate()
  const [cookies] = useCookies()
  const stateCookieName = process.env.REACT_APP_STATE_COOKIE_NAME as string
  const isLoggedIn = cookies[stateCookieName] !== undefined
  const traceId = cookies.sa_trace

  const errorMapped = errorCode && errorMap[errorCode] ? errorMap[errorCode] : errorMap['500']

  usePageTitle(t(errorMapped.title))

  const renderMainButton = (format: string) => {
    if (format === 'A') {
      return undefined
    }

    const buttonType = errorMapped.button

    if (buttonType === 'samsungAccountHome') {
      return (
        <Button color='secondary' onClick={() => navigate('/')} data-testid='Button1729BFF7'>
          {t('iam.01.Common.Button.Samsung.account.home')}
        </Button>
      )
    } else if (buttonType === 'ok') {
      return (
        <Button color='secondary' onClick={() => navigate(errorMapped?.nextUrl || '/')} data-testid='Button89F1FA35'>
          {t('iam.01.Common.Button.OKAY')}
        </Button>
      )
    } else if (buttonType === 'previous') {
      return (
        <Button color='secondary' onClick={() => navigate('/')} data-testid='Button1AE0B48F'>
          {t('iam.01.Common.Button.Previous.page')}
        </Button>
      )
    } else {
      return undefined
    }
  }

  const renderCustomerSupportButton = (format: string) => {
    if (format !== 'C') {
      return undefined
    }

    const url = isLoggedIn
      ? `https://help.content.samsung.com/csweb/auth/gosupport.do?targetUrl=/main/main.do&serviceCd=saccount&_common_country=${i18n.countryCode}&_common_lang=${i18n.languageCode}`
      : 'https://help.content.Samsung.com/csweb/faq/searchFaq.do'

    return (
      <Button color='secondary' onClick={() => (window.location.href = url)} data-testid='Button1E8801C0'>
        {t('iam.01.Common.Footer.Customer.Support')}
      </Button>
    )
  }

  const renderAction = () => {
    const format = errorMapped.format || 'C'

    const mainButton = renderMainButton(format)
    const customerSupportButton = renderCustomerSupportButton(format)

    if (mainButton === undefined && customerSupportButton === undefined) {
      return undefined
    }

    return (
      <Box sx={{ display: 'flex', flexDirection: 'column', gap: '12px' }}>
        {mainButton}
        {customerSupportButton}
      </Box>
    )
  }

  return (
    <Container size='small'>
      <Section
        sx={{ marginTop: { xs: '30px', md: '60px' } }}
        title={t(errorMapped.title)}
        titleSx={{ fontWeight: 400 }}
        description={
          <>
            <Box sx={{ marginBottom: '23px' }}>
              <InnerHtml variant='body1' sx={{ fontWeight: '400' }}>
                {t(errorMapped.message)}
              </InnerHtml>
            </Box>
            <Box sx={{ marginBottom: '8px' }}>
              <Typography variant='body1' color='info.light' sx={{ lineBreak: 'anywhere' }}>
                {errorMapped?.errorCode ? `(${errorMapped.errorCode}) ${traceId}` : traceId}
              </Typography>
            </Box>
            <Box>
              <Typography variant='body1' color='info.light'>
                {t('iam.ETC.Common.Error.Message.Sharing.this.code.with.Customer.Support.may.help.them.solve.this.issue.for.you')}
              </Typography>
            </Box>
          </>
        }
        actions={renderAction()}
      ></Section>
    </Container>
  )
}

export default ErrorPage
